import type { PostFlightOpportunity } from "@/features/post-flight/api/post-flight.api";
import { humanizeConceptLabel } from "@/features/personas/lib/persona-format";

/** Winning-ad figures stored on a Post-Flight opportunity at double-down creation. */
export interface PostFlightWindowContextData {
  asOf?: string | null;
  firstSeen?: string | null;
  windowStart?: string | null;
  windowEnd?: string | null;
  windowDays?: number | null;
  cpaFirstHalf?: number | null;
  cpaSecondHalf?: number | null;
}

/** Winning-ad figures stored on a Post-Flight opportunity at double-down creation. */
export interface PostFlightOpportunityMetrics {
  instruction: string | null;
  parentTitle: string | null;
  cpa: number | null;
  accountCpa: number | null;
  pctBetter: number | null;
  purchases: number | null;
  spend: number | null;
  adRank: number | null;
  cpaFirstHalf: number | null;
  cpaSecondHalf: number | null;
  asOf: string | null;
  firstSeen: string | null;
  windowStart: string | null;
  windowEnd: string | null;
  windowDays: number | null;
  daysLive: number | null;
  /** CPA moved down, up, or stayed level across the two window halves. */
  trend: "improving" | "worsening" | "flat" | null;
}

function numberOrNull(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function textOrNull(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function trendFromHalves(first: number | null, second: number | null): PostFlightOpportunityMetrics["trend"] {
  if (first == null || second == null) return null;
  if (second < first) return "improving";
  if (second > first) return "worsening";
  return "flat";
}

/** Reads the double-down metadata blob. Other set types do not carry these keys. */
export function readPostFlightMetrics(metadata: unknown): PostFlightOpportunityMetrics | null {
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) return null;
  const record = metadata as Record<string, unknown>;
  const hasSignal =
    "instruction" in record || "parent_hash" in record || "cpa" in record || "parent_title" in record;
  if (!hasSignal) return null;
  const parentTitle = textOrNull(record.parent_title) ?? textOrNull(record.parent_concept);
  return {
    instruction: textOrNull(record.instruction),
    parentTitle: parentTitle ? humanizeConceptLabel(parentTitle) : null,
    cpa: numberOrNull(record.cpa),
    accountCpa: numberOrNull(record.account_cpa),
    pctBetter: numberOrNull(record.pct_better),
    purchases: numberOrNull(record.purchases),
    spend: numberOrNull(record.spend),
    adRank: numberOrNull(record.ad_rank),
    cpaFirstHalf: numberOrNull(record.cpa_first_half),
    cpaSecondHalf: numberOrNull(record.cpa_second_half),
    asOf: textOrNull(record.as_of),
    firstSeen: textOrNull(record.first_seen),
    windowStart: textOrNull(record.window_start),
    windowEnd: textOrNull(record.window_end),
    windowDays: numberOrNull(record.window_days),
    daysLive: numberOrNull(record.days_live),
    trend: trendFromHalves(numberOrNull(record.cpa_first_half), numberOrNull(record.cpa_second_half)),
  };
}

export function formatPostFlightMoney(value: number): string {
  return `$${Math.round(value).toLocaleString("en-US")}`;
}

/** `pct_better` is a fraction, matching the Post-Flight page (`0.12` → `12%`). */
export function formatPostFlightPctBetter(value: number): string {
  return `${Math.round(value * 100)}%`;
}

const EMPTY_POST_FLIGHT_METRICS: PostFlightOpportunityMetrics = {
  instruction: null,
  parentTitle: null,
  cpa: null,
  accountCpa: null,
  pctBetter: null,
  purchases: null,
  spend: null,
  adRank: null,
  cpaFirstHalf: null,
  cpaSecondHalf: null,
  asOf: null,
  firstSeen: null,
  windowStart: null,
  windowEnd: null,
  windowDays: null,
  daysLive: null,
  trend: null,
};

/** Test and story fixtures — all window/snapshot fields default to null. */
export function postFlightMetricsFixture(
  overrides: Partial<PostFlightOpportunityMetrics> = {},
): PostFlightOpportunityMetrics {
  return { ...EMPTY_POST_FLIGHT_METRICS, ...overrides };
}

export function postFlightWindowContextFromMetrics(
  metrics: PostFlightOpportunityMetrics,
): PostFlightWindowContextData {
  return {
    asOf: metrics.asOf,
    firstSeen: metrics.firstSeen,
    windowStart: metrics.windowStart,
    windowEnd: metrics.windowEnd,
    windowDays: metrics.windowDays,
    cpaFirstHalf: metrics.cpaFirstHalf,
    cpaSecondHalf: metrics.cpaSecondHalf,
  };
}

export function postFlightWindowContextFromListRow(
  row: PostFlightOpportunity | undefined,
  listAsOf: string | null,
): PostFlightWindowContextData {
  if (!row) return {};
  return {
    asOf: listAsOf,
    firstSeen: row.first_seen ?? null,
    windowStart: row.window_start ?? null,
    windowEnd: row.window_end ?? null,
    windowDays: row.window_days,
    cpaFirstHalf: row.cpa_first_half ?? null,
    cpaSecondHalf: row.cpa_second_half ?? null,
  };
}
